import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { useOptimize, useTelemetry } from '@figure/frontlib-growth';
import {
  usePlaid,
  useAch,
  useOffer,
  usePointserv,
  usePersonalLoanRouteFsm,
  usePersonalLoan,
  usePointservListener,
  useTrueworkJS,
} from 'shared/hooks';
import { getContextUrl } from 'shared/utils';
import { Link } from 'shared/constants';
import {
  AssetInstruction,
  CheckingInstruction,
  PaystubInstruction,
  PointservModal,
  TaxInstruction,
} from './';

const LinkIncomeContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  // @ts-ignore
  const { uuid: personalLoanUuid, type: optionType } = useParams();
  const [plaidError, setPlaidError] = useState('');
  const reqRecLength = useRef();
  const [trueworkError, setTrueworkError] = useState('');
  const [pointservOpen, setPointservOpen] = useState(false);
  const { personalLoan } = usePersonalLoan();
  const { optimizeActivate, experimentsByKey } = useOptimize();
  const { telemetry } = useTelemetry();

  const { loaded: plaidLinkLoaded, error: plaidLinkError, create: plaidLinkCreate } = usePlaid();
  const {
    url,
    orderId,
    generateUrl,
    completeFlow,
    clearUrl,
    serverError: pointservError,
  } = usePointserv();
  const { twCreate } = useTrueworkJS();
  const { getOfferPackage } = useOffer();
  const {
    savePlaidAuthLink,
    getPlaidAssets,
    saveFailedPlaidLink,
    serverError: achError,
  } = useAch();
  const runRouter = usePersonalLoanRouteFsm();

  const PL_TRUEWORK_PAYSTUB = 'pl_truework_paystub';
  const experiment = experimentsByKey[PL_TRUEWORK_PAYSTUB];

  const serverError = achError || pointservError || trueworkError || '';

  const pointservContext = {
    orderId,
    url: url
      ? `${url}&ReturnURL=${getContextUrl()}/applications/${personalLoanUuid}/income/pointserv-callback`
      : '',
  };

  const onLoad = useCallback(async () => {
    if (personalLoan.status?.uwSubDecision === 'VERIFIED_INCOME_MATCHED_OFFER') {
      return runRouter();
    }
  }, []);

  const paystubOptionsMap = {
    TRUEWORK: 'plIncomeVerifiedTruework',
    POINTSERV: 'plIncomeVerifiedPointserv',
  };

  useEffect(() => {
    optimizeActivate(PL_TRUEWORK_PAYSTUB);
  }, [optimizeActivate]);

  useEffect(() => {
    (async () => {
      await onLoad();
    })();
  }, [onLoad]);

  useEffect(() => {
    if (pointservOpen && !pointservContext.url) {
      telemetry(`${paystubOptionsMap.POINTSERV}: Close: No URL`);
      setPointservOpen(false);
    }
  }, [pointservOpen, pointservContext.url]);

  useEffect(() => {
    !!pointservError && telemetry(`${paystubOptionsMap.POINTSERV}: Error`);
  }, [pointservError]);

  const onPointservCallback = async (orderId) => {
    let count;
    const pointservCallback = async (dispatch, tries = 0) => {
      if (tries > 5) throw Error('Unable to complete pointserv flow');

      try {
        // get newest pointserv request uuid
        const { incomeRequestRecords } = await getOfferPackage(personalLoanUuid);
        count = incomeRequestRecords.length;

        return await completeFlow(orderId);
      } catch (e) {
        return pointservCallback(dispatch, tries + 1);
      }
    };

    await pointservCallback(dispatch);

    const MAX_ATTEMPTS = 5;
    const pollOfferPackage = async (attempts = 0) => {
      if (attempts < MAX_ATTEMPTS) {
        const { incomeRequestRecords } = await getOfferPackage(personalLoanUuid);

        if (incomeRequestRecords.length === count) return await pollOfferPackage(attempts + 1);
      }

      history.push(`/applications/${personalLoanUuid}/income/review`);
    };

    await pollOfferPackage();
  };

  const getRequestRecordsLength = async (maxAttempts = 10) => {
    const trueworkCallback = async (tries = 0) => {
      if (tries > maxAttempts) throw Error('Unable to complete truework flow');

      try {
        // get newest paystub request uuid
        const { incomeRequestRecords } = await getOfferPackage(personalLoanUuid);
        reqRecLength.current = incomeRequestRecords.length;
      } catch (e) {
        return trueworkCallback(tries + 1);
      }
    };

    await trueworkCallback();
  };

  const onTrueworkCallback = async (reqLength, maxAttempts = 10) => {
    const pollOfferPackage = async (attempts = 0) => {
      if (attempts < maxAttempts) {
        setTimeout(async () => {
          const { incomeRequestRecords } = await getOfferPackage(personalLoanUuid);
          if (incomeRequestRecords.length <= reqLength) {
            return await pollOfferPackage(attempts + 1);
          } else {
            history.push(`/applications/${personalLoanUuid}/income/review`);
          }
        }, 2000);
      }

      return null;
    };

    await pollOfferPackage();

    return null;
  };

  usePointservListener(pointservContext.orderId, onPointservCallback, () => {
    telemetry(`${paystubOptionsMap.POINTSERV}: Close: Success`);
    onPointservReset();
    setPointservOpen(false);
  });

  const openPlaidLink = () =>
    plaidLinkCreate({
      onSuccess: async (accessToken, metadata) => {
        try {
          await onPlaidSuccess(accessToken, metadata);
        } catch (e) {
          setPlaidError('Unable to retrieve accounts');
        }
      },
      onExit: (error, meta) => {
        if (error) onPlaidError(error, meta);
      },
    });

  const onPointservLink = async (type) => {
    await generateUrl(personalLoanUuid, {
      tax: type === Link.TAX,
      payroll: type === Link.PAYSTUB,
      appType: 'PL_APP',
    });
  };

  const openPointservLink = async (type) => {
    telemetry(`${paystubOptionsMap.POINTSERV}/${type}: Open `);
    await onPointservLink(type);
    setPointservOpen(true);
  };

  const openTrueworkLink = () => {
    telemetry(paystubOptionsMap.TRUEWORK);

    const onOpen = () => {
      telemetry(`${paystubOptionsMap.TRUEWORK}: Open`);
      getRequestRecordsLength();
      setTrueworkError('');
    };
    const onClose = () => {
      telemetry(`${paystubOptionsMap.TRUEWORK}: Close`);
    };
    const onSuccess = () => {
      telemetry(`${paystubOptionsMap.TRUEWORK}: Success`);
      onTrueworkCallback(reqRecLength.current);
      setTrueworkError('');
    };
    const onError = (e) => {
      telemetry(`${paystubOptionsMap.TRUEWORK}: Error`);
      setTrueworkError(
        'We are having an issue connecting to payroll provider(s) at the moment.  Please try another Income Verification method or contact us.'
      );
    };

    // @ts-ignore
    twCreate({ onOpen, onClose, onSuccess, onError });
  };

  const onBack = () => history.push(`/applications/${personalLoanUuid}/income/review`);

  const onPointservReset = () => clearUrl();

  const onPlaidSuccess = async (accessToken, metaData) => {
    await savePlaidAuthLink(accessToken, metaData);
    await getPlaidAssets();

    history.push(`/applications/${personalLoanUuid}/income/review`);
  };

  const onPlaidError = async (linkError, linkErrorMetadata) =>
    saveFailedPlaidLink({ linkError, linkErrorMetadata });

  const renderInstruction = (type) => {
    const error = serverError || plaidError || plaidLinkError || '';

    switch (type) {
      case Link.ASSET:
        return (
          <AssetInstruction handleBack={onBack} handleClick={openPlaidLink} serverError={error} />
        );
      case Link.PAYSTUB:
        return (
          <PaystubInstruction
            handleBack={onBack}
            handleClick={() =>
              experiment.isReady &&
              (experiment.variation === 'variation'
                ? openTrueworkLink()
                : openPointservLink(Link.PAYSTUB))
            }
            serverError={error}
          />
        );
      case Link.CHECKING:
        return (
          <CheckingInstruction
            handleBack={onBack}
            handleClick={openPlaidLink}
            serverError={error}
          />
        );
      case Link.TAX:
        return (
          <TaxInstruction
            handleBack={onBack}
            handleClick={() => openPointservLink(Link.TAX)}
            serverError={error}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {plaidLinkLoaded && renderInstruction(optionType)}

      <PointservModal
        isOpen={pointservOpen}
        url={pointservContext.url}
        verificationType={optionType}
        handleClose={() => {
          onPointservReset();
          setPointservOpen(false);
          telemetry(`${paystubOptionsMap.POINTSERV}: Closed By User`);
        }}
      />
    </>
  );
};

export default LinkIncomeContainer;
